/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {translate} from '@ion/core/src/locale/locale';
import {IApplicationFeatures} from '@ion/core/src/security/ApplicationFeatures';
import {OPTIONS} from '@ion/core/src/services/Reports';
import {
  Entity,
  Feature,
  GenericObject,
  ModuleActions
} from '@ion/core/src/types/entities';
import {Operation} from '@ion/core/src/types/general';
import {ACTIONS} from './helpers';
import {PermissionsOptions} from './PermissionsUtils';

/**
 * Defines the possible actions related to provisioning and file management
 * within the application. Each member of the enum represents a specific action.
 */
enum PROVISIONING_ACTIONS {
  PROVISIONING = 'PROVISIONING',
  PROVISIONING_DOWNLOAD_JOBS = 'PROVISIONING_DOWNLOAD_JOBS',
  PROVISIONING_DEVICE_FILES = 'PROVISIONING_DEVICE_FILES',
  DEVICE_FILES_CREATE = 'DeviceFiles-Create-1',
  JOBS_CREATE = 'Jobs-Create-1',
  FILE_PERMISSIONS = 'FILE_PERMISSIONS'
};

//Defined the Service Enablement Default Feature Action Ids
export const DEVICES_UPDATE_ACTION = 'Devices-Update-3';
export const ACCOUNTS_READ_ACTION  = 'Accounts-Read-2';
  
//Define Provisioneed Devices Delete action id.
export const MANUFACTURED_DEVICES_DELETE_ACTION = 'ManufacturingInventory-Delete-4';

//Define Provisioneed Devices Update action id.
export const MANUFACTURED_DEVICES_UPDATE_ACTION = 'ManufacturingInventory-Update-3';

//Define RMA Feature Action id.
export const RMA_FEATURE_ACTION = 'ManufacturingRMA-Delete-4';

//Define Device & Assets Action ids.
export const DEVICES_READ_ACTION  = 'Devices-Read-2';
export const ASSETS_READ_ACTION   = 'Assets-Read-2';
export const ASSETS_CREATE_ACTION = 'Assets-Create-1';
export const ASSETS_UPDATE_ACTION = 'Assets-Update-3';

//Define the service carb id
const SERVICES_CARB_UPDATE = 'ServicesCarb-Update-3';
const CARB_UPDATE          = 'CARB-Update-3';

/**
 * Define the all action
 */
export const ALL_ACTION = 'All';

//Define the Report Categories Feature Action id (its child Report type Action ids are derived
//from REPORT_CATEGORY_MODULES, see REPORT_CATEGORIES_CHILD_ACTIONS).
export const REPORT_CATEGORIES_ALL_ACTION = 'ReportCategories-All-0';

//Define the Report Scheduler (Analytics & Reports) and Report Scheduler Beta (Early Access) Action ids.
export const REPORTS_SCHEDULER_ACTION         = 'ReportScheduler-All-0';
export const REPORT_SCHEDULER_BETA_ACTION     = 'ReportSchedulerBeta-All-0';
export const REPORT_SCHEDULER_STANDARD_ACTION = 'ReportScheduleDirector-All-0';
export const REPORT_SCHEDULER_ROLE_ACTIONS    = [
  'ReportScheduleDriver-All-0',
  REPORT_SCHEDULER_STANDARD_ACTION,
  'ReportSchedulerFleetManager-All-0'
];

/**
 * Resolves the Report Scheduler 2.0 tier (Basic / Standard / Admin) actions which are disabled
 * in the Role Editor, as only one tier can be tagged at a time: once any tier is tagged, every
 * other tier is disabled.
 *
 * ```typescript
 * import {getDisabledReportScheduleTiers} from './ModuleActionHelpers';
 * ...
 *
 * // disables the basic and admin tiers while standard is tagged...
 * getDisabledReportScheduleTiers([REPORT_SCHEDULER_STANDARD_ACTION]);
 * ```
 *
 * @param checkedTiers  The tier action ids which are currently tagged.
 * @returns             The tier action ids to disable, or an empty array when no tier is tagged.
 */
export const getDisabledReportScheduleTiers = (checkedTiers: string[] = []): string[] => (
  checkedTiers.length
    ? REPORT_SCHEDULER_ROLE_ACTIONS.filter(id => !checkedTiers.includes(id))
    : []
);

//Define the Action ids which require Devices & Accounts Read to be tagged.
export const DEVICES_ACCOUNTS_READ_DEPENDENT_ACTIONS = [
  REPORT_CATEGORIES_ALL_ACTION,
  REPORTS_SCHEDULER_ACTION,
  REPORT_SCHEDULER_BETA_ACTION
];

/**
 * Determines if the Devices Read and Accounts Read dependency of the Report Categories,
 * Report Scheduler and Report Scheduler 2.0 actions is broken, i.e. any of those actions
 * is tagged while Devices Read or Accounts Read is un-tagged.
 *
 * ```typescript
 * import {hasReportsDependencyError} from './ModuleActionHelpers';
 * ...
 *
 * // blocks the role submission while accounts read is un-tagged...
 * hasReportsDependencyError([REPORTS_SCHEDULER_ACTION, DEVICES_READ_ACTION]);
 * ```
 *
 * @param checkedActions  The action ids which are currently tagged.
 * @returns               `true` when a report action is tagged without Devices Read
 *                        and Accounts Read, otherwise `false`.
 */
export const hasReportsDependencyError = (checkedActions: string[] = []): boolean => (
  DEVICES_ACCOUNTS_READ_DEPENDENT_ACTIONS.some(id => checkedActions?.includes(id)) &&
  ![DEVICES_READ_ACTION, ACCOUNTS_READ_ACTION].every(id => checkedActions?.includes(id))
);

/**
 * Resolves the rendered Role Editor checkbox of the given feature action id.
 *
 * ```typescript
 * import {getActionCheckbox} from './ModuleActionHelpers';
 * ...
 *
 * // auto-tags devices read when it is rendered and un-tagged...
 * const checkbox = getActionCheckbox(DEVICES_READ_ACTION);
 * ```
 *
 * @param id  The feature action id (e.g. `Devices-Read-2`).
 * @returns   The checkbox input element, or `null` when it is not rendered.
 */
export const getActionCheckbox = (id: string): HTMLInputElement | null => (
  document.getElementById(id) as HTMLInputElement | null
);

/**
 * Determines if the rendered Role Editor checkbox of the given feature action id is checked.
 *
 * ```typescript
 * import {isActionChecked} from './ModuleActionHelpers';
 * ...
 *
 * // resolves the tagged report scheduler 2.0 tiers...
 * const checkedTiers = REPORT_SCHEDULER_ROLE_ACTIONS.filter(isActionChecked);
 * ```
 *
 * @param id  The feature action id (e.g. `Devices-Read-2`).
 * @returns   `true` when the checkbox is rendered and checked, otherwise `false`.
 */
export const isActionChecked = (id: string): boolean => Boolean(getActionCheckbox(id)?.checked);

/**
 * Determines if any of the rendered Role Editor checkboxes of the given feature action ids
 * is checked.
 *
 * ```typescript
 * import {hasCheckedAction} from './ModuleActionHelpers';
 * ...
 *
 * // keeps report categories tagged while any report category is tagged...
 * hasCheckedAction(REPORT_CATEGORIES_CHILD_ACTIONS);
 * ```
 *
 * @param ids  The feature action ids.
 * @returns    `true` when any of the checkboxes is checked, otherwise `false`.
 */
export const hasCheckedAction = (ids: string[]): boolean => Boolean(ids?.some(isActionChecked));

/**
 * Provide a reference to all pre-defined ADM Application features and sub features
 * for the ADM application.
 */
export const MODULES = {
  ADMIN_ACCOUNTS                     : 'Accounts',
  ADMIN_AEMP_CONFIG                  : 'AEMPConfig',
  ADMIN_APPLICATIONS                 : 'Applications',
  ADMIN_ASSETS                       : 'Assets',
  ADMIN_GROUPS                       : 'Groups',
  ADMIN_PEG_BEHAVIORS                : 'PEGBehavior',
  ADMIN_PEG_LABELS                   : 'PEGLabels',
  ADMIN_ROLES                        : 'Roles',
  ADMIN_USERS                        : 'Users',
  ADMIN_DEVICE_CONFIGS               : 'DeviceConfigs',
  ALERTS_NOTIFICATIONS               : 'AlertNotification',
  ALERTS_SETUP                       : 'AlertSetup',
  DEVICES                            : 'Devices',
  DASHBOARD_PAGE                     : 'DashBoard',
  PROVISIONING_DEVICE_FILES          : 'DeviceFiles',
  PROVISIONING_DOWNLOAD_JOBS         : 'Jobs',
  PROVISIONING_JOB_SEARCH_ENGINE     : 'JobSearchEngine',
  REPORTS_AVAILABLE_REPORTS          : 'AvailableReports',
  REPORT_MANAGER_PAGE                : 'ReportScheduler',
  REPORTS_SCHEDULER                  : 'ReportScheduler',
  REPORTS_CATEGORIES                 : 'ReportCategories',
  DEVICE_MANAGEMENT                  : 'DeviceManagement',
  VEHICLE_MANAGEMENT                 : 'VehicleManagement',
  ADMIN_REPORTS                      : 'AdminReports',
  CARB_REPORTS                       : 'CARBReports',
  CRASHBOXX_REPORTS                  : 'CrashboxxReports',
  DASHBOARDS_REPORTS                 : 'DashboardsReports',
  SYSTEM_CONFIG_ALERT_TYPES          : 'AlertTypes',
  SYSTEM_CONFIG_APPLICATIONS         : 'SystemApplications',
  SYSTEM_CONFIG_CARRIERS             : 'Carriers',
  SYSTEM_CONFIG_DEVICE_TYPES         : 'DeviceTypes',
  SYSTEM_CONFIG_CARRIER_PART_NUMBERS : 'CarrierPartNumbers',
  TOOLBOX_KEYFILE_BUILDER            : 'KeyfileBuilder',
  TOOLBOX_MODBUS                     : 'ModBus',
  MANUFACTURED_DEVICES               : 'ManufacturedDevices',
  MANUFACTURING_INVENTORY            : 'ManufacturingInventory',
  MANUFACTURING_APPLICATION_IDS      : 'ManufacturingApplicationIds',
  MANUFACTURING_PART_NUMBERS         : 'ManufacturingPartNumbers',
  MANUFACTURING_SHIPMENTS            : 'ManufacturingShipments',
  MANUFACTURING_RMA                  : 'ManufacturingRMA',
  VEHICLE_MANAGER                    : 'VehicleManager',
  VEHICLE_PROFILE                    : 'ProfileManager',
  OEM_VALIDATION_RULES               : 'Validation',
  FILE_PERMISSIONS_SCRIPT            : 'Script',
  FILE_PERMISSIONS_LMU_FW_CO_PRO     : 'LMUFirmwareAndCoProcessor',
  FILE_PERMISSIONS_VBUS              : 'Vbus',
  FILE_PERMISSIONS_BLUE_TOOTH        : 'Bluetooth',
  FILE_PERMISSIONS_RADIO_FW          : 'RadioFW',
  FILE_PERMISSIONS_HOSTED_EDGE_OPKGS : 'HostedAppAndEdgeAppAndOPKGS',
  FILE_PERMISSIONS_CUSTOMER_DB_FILES : 'CustomerDatabaseFiles',
  FILE_PERMISSIONS_MDT               : 'MDT',
  FILE_PERMISSIONS_KEY_FILES         : 'KeyFiles',
  SENSOR_INSTALLER                   : 'SensorInstaller',
  SERVICES_CARB                      : 'ServicesCarb',
  SENTINEL                           : 'Sentinel',
  TELECRYPT                          : 'Telecrypt',
  BLE                                : 'BLE',
  BMS                                : 'BMS',
  CARGO                              : 'CARGO',
  DOOR                               : 'DOOR',
  EA                                 : 'EA',
  ICN                                : 'ICN',
  IVT                                : 'IVT',
  LMS                                : 'LMS',
  TPMS                               : 'TPMS',
  WETS                               : 'WETS',
  WMS                                : 'WMS',
  CARB                               : 'CARB',
  MODBUS                             : 'MODBUS',
  REPORT_SCHEDULER_BETA              : 'ReportSchedulerBeta',
  REPORT_SCHEDULER_BASIC             : 'ReportScheduleDriver',
  REPORT_SCHEDULER_STANDARD          : 'ReportScheduleDirector',
  REPORT_SCHEDULER_ADMIN             : 'ReportSchedulerFleetManager'
};

//Define the Report Categories sub feature (Report type) modules.
export const REPORT_CATEGORY_MODULES = [
  MODULES.DEVICE_MANAGEMENT,
  MODULES.VEHICLE_MANAGEMENT,
  MODULES.ADMIN_REPORTS,
  MODULES.CARB_REPORTS,
  MODULES.CRASHBOXX_REPORTS,
  MODULES.DASHBOARDS_REPORTS
];

//Define the Report Categories child (All) Action ids, i.e. the rendered checkbox id of each Report type.
export const REPORT_CATEGORIES_CHILD_ACTIONS = REPORT_CATEGORY_MODULES.map(name => `${name}-${ALL_ACTION}-0`);

/**
 * Define the module name with feature id associated.
 * todo -> This needs to be removed once the module action
 * clean up done(ADM-2853).
 */
export const MAP_MODULES_FEATURE_IDS = {
  'cbdf8164-d7bf-4032-a2e9-a99a10e006f8' : 'Accounts',
  'd1b09313-ec13-4af9-9d05-6e66fe3e07c1' : 'AccountsServiceProfile',
  'fcefbe1d-40b3-463f-85a3-239aa5d3188e' : 'AEMPConfig',
  '1972c656-b472-49e1-9ab0-ab0c69640455' : 'Applications',
  '56d5bc66-0782-4d59-8f88-0e6c683535f9' : 'Assets',
  'ffee852d-6627-4002-a67e-6ce27171b5b9' : 'Groups',
  '16099af0-d13d-4a4a-9dd7-39734a0aaede' : 'PEGBehavior',
  'dc20f01d-446a-4295-8e54-9b56fb992a1a' : 'PEGLabels',
  'f24d4d45-b0bf-4d8f-a8de-212dfcf5c685' : 'Roles',
  '926fd6ef-161e-43f5-8491-c2ea3861ece2' : 'Users',
  '45d19b6d-9356-4578-8f50-44a92582e2bb' : 'DeviceConfigs',
  'dfda6bc3-80b0-4a44-8b81-d0fb0e930e7f' : 'AlertNotification',
  'c1bc107c-4023-4c41-95c0-2509c0b3575f' : 'AlertSetup',
  '57f510b3-4b9c-4a63-bf26-337fd6bf3c58' : 'Devices',
  'd45d4cd5-6de4-428b-a946-477424d6f585' : 'DashBoard',
  '6f19aadf-40a7-426e-89eb-e7edbf598c0f' : 'DeviceFiles',
  '6916303b-9020-4b68-830f-160ce4d011eb' : 'Jobs',
  '49a543f7-6e64-48ff-98aa-8d1c5fa70e9c' : 'AvailableReports',
  '26e49e3f-e2d1-48ed-9edf-e4584f770c7f' : 'ScheduledReports',
  '01b3da29-ffbb-415c-94d2-f9bc7d809fae' : 'ReportScheduler',
  '07af8a95-bd84-470d-9cd7-18bb642ccbac' : 'ReportCategories',
  '86b26c9b-6727-4bf6-95d7-6c685e3f2d79' : 'DeviceManagement',
  '09ddbb68-d024-4cf8-9e05-fcc92fa6fefa' : 'VehicleManagement',
  '1503bdad-194c-4109-86aa-840bb4c4d55a' : 'AdminReports',
  'e88fbb0f-3fc2-4371-8175-2634e8d1d315' : 'CARBReports',
  'e400fe00-b771-4b29-af51-ec04f1159b85' : 'CrashboxxReports',
  '3a9578a6-4cd0-4f9b-a53a-948503364bde' : 'DashboardsReports',
  '320ceaff-851c-41ac-b5d3-60cd768228bb' : 'AlertTypes',
  'a9e8d8d2-ca04-4f28-89a7-cca03ea868b8' : 'SystemApplications',
  '81753b26-00a6-44af-bb34-ac0213c1ec7c' : 'Carriers',
  '697cb690-4834-4008-ba6e-3ffa18c26075' : 'DeviceTypes',
  '72419b98-9ce0-4620-9c4a-15c8d088df9d' : 'CarrierPartNumbers',
  '6d94c792-d32c-43b6-ba4f-2404c5e4e157' : 'KeyfileBuilder',
  'a83c7fd1-9e26-4b7d-b539-283d6b4f9c15' : 'ManufacturedDevices',
  'e92b6da4-7f36-2c1b-a849-793d6b5f2c46' : 'ManufacturingInventory',
  'b73d6fa2-1e96-4c7b-a539-283d6b4f9c15' : 'ManufacturingApplicationIds',
  'f93e8bd3-2b19-7c6b-a958-963d1b2c4f46' : 'ManufacturingPartNumbers',
  'ae052d7d-a242-f5ce-2bb6-2bef59f4a1b1' : 'ManufacturingShipments',
  '3d3f464d-6234-4b23-aead-7f7f70d02450' : 'ManufacturingRMA',
  'd5972faa-834c-4c1e-a18d-a42cf3e3cb80' : 'Script',
  '20e4f2b8-966c-4ac3-ba67-d2ea9c62d7da' : 'LMUFirmwareAndCoProcessor',
  '227eb22e-79e0-4072-917b-75e02526d9d7' : 'Vbus',
  '78bd342a-3af4-477d-8f45-a429353fbbda' : 'Bluetooth',
  '97f5dd2a-7112-4924-8311-1dab797cf5ef' : 'RadioFW',
  '4ad1dd42-4046-431a-8115-260ff597216d' : 'HostedAppAndEdgeAppAndOPKGS',
  '71bb275e-c8db-44ec-8eec-3c10b4a12a24' : 'CustomerDatabaseFiles',
  'c87350dc-851e-4bad-a60d-ce9dc1b62ad8' : 'MDT',
  '4f52d226-70f0-40e3-94b4-9b8549c4f829' : 'KeyFiles',
  '1bf27ac7-f091-4abb-99b8-15c9467d0fde' : 'VehicleManager',
  '3c2939ce-caee-4fa8-97e4-c66b61dc8a34' : 'ProfileManager',
  '1ddc6d62-62a9-496a-9de7-75f78320ccc5' : 'Validation',
  'fb2419cb-ef00-434d-8aa3-a46090bf15ff' : 'SensorInstaller',
  'f71c1cdc-377c-4c7a-8943-b753619b675b' : 'ServicesCarb',
  '6158d42d-4c26-4acc-a406-11dca2976a38' : 'ModBus',
  '824611cd-b768-4b27-9b89-e0a0cd638c0d' : 'Sentinel',
  '4e0e8581-6698-4ff7-9fff-820bc02bb658' : 'Telecrypt',
  'd3d343be-9082-4845-ad8a-7a53e2c3e41d' : 'BLE',
  'e39ed71f-6da4-49ad-ba73-a5beff2b14f4' : 'BMS',
  '3901a8a6-1e09-4c3f-b0ca-2791f4a6848c' : 'CARGO',
  '5b2b0fb3-6455-43ce-ae3b-22904bf3379a' : 'DOOR',
  '3615263c-d713-4bf0-bda0-fa7543e90b42' : 'EA',
  'b236f286-f40b-4902-ad11-714bb5dd2bad' : 'ICN',
  'c9af65e4-c6f3-4e1d-ad6b-60408c69e7f5' : 'IVT',
  '43d5ee37-4fdf-4376-9f41-dd3414dd83d7' : 'LMS',
  'bd8933f0-bf49-4412-b7f2-a944ad47d37d' : 'TPMS',
  'a4106ce9-d904-41c7-aa3c-b23eda5cce56' : 'WETS',
  '1b1a88d2-124b-4a89-9d92-570dfbad976d' : 'WMS',
  '6e331607-800c-4b02-9c44-5dab6ca9c218' : 'CARB',
  '12f236d9-2f2e-45bb-9356-bc3727f0266f' : 'MODBUS',
  '7a5b18e5-bb17-4313-8c5f-3cb9e9f575bc' : 'ReportSchedulerBeta',
  'fa45d0c3-95be-4357-b281-589c96e2e7df' : 'ReportScheduleDriver',
  'de49ff39-1472-4218-acd9-083ad74144da' : 'ReportScheduleDirector',
  '22670df8-8723-4ab8-aaf3-56a335378a6c' : 'ReportSchedulerFleetManager'

};

/**
 * Alias for the application feature names which is having multiple words. Also the
 * Alerts and Reports are coming under 'AlertSetup' and 'AvailableReports' in ADM.
 * So the DOCTC/DOOC Reports and Alerts will be updated under modules as
 * 'AlertSetup' and 'AvailableReports' respectively.
 */
export const MODULE_ALIAS = {
  'AEMP Config'           : MODULES.ADMIN_AEMP_CONFIG,
  'Alert Types'           : MODULES.SYSTEM_CONFIG_ALERT_TYPES,
  Alerts                  : MODULES.ALERTS_SETUP,
  'Alerts Notification'   : MODULES.ALERTS_NOTIFICATIONS,
  'Alerts Setup'          : MODULES.ALERTS_SETUP,
  'Available Reports'     : MODULES.REPORTS_AVAILABLE_REPORTS,
  Dashboard               : MODULES.DASHBOARD_PAGE,
  'Device Configurations' : MODULES.ADMIN_DEVICE_CONFIGS,
  'Device Files'          : MODULES.PROVISIONING_DEVICE_FILES,
  'Device Types'          : MODULES.SYSTEM_CONFIG_DEVICE_TYPES,
  'Carrier Part Numbers'  : MODULES.SYSTEM_CONFIG_CARRIER_PART_NUMBERS,
  'Download Jobs'         : MODULES.PROVISIONING_DOWNLOAD_JOBS,
  'Job Search Engine'     : MODULES.PROVISIONING_JOB_SEARCH_ENGINE,
  'PEG Behaviors'         : MODULES.ADMIN_PEG_BEHAVIORS,
  'PEG Labels'            : MODULES.ADMIN_PEG_LABELS,
  'Scheduled Reports'     : MODULES.REPORTS_AVAILABLE_REPORTS,
  'Keyfile Builder'       : MODULES.TOOLBOX_KEYFILE_BUILDER,
  'Manufactured Devices'  : MODULES.MANUFACTURED_DEVICES,
  Inventory               : MODULES.MANUFACTURING_INVENTORY,
  'Application Ids'       : MODULES.MANUFACTURING_APPLICATION_IDS,
  'Part Numbers'          : MODULES.MANUFACTURING_PART_NUMBERS
};

const SINGLE_CHECKBOX_FEATURES = [
  MODULE_ALIAS.Dashboard,
  MODULE_ALIAS['Alerts Notification'],
  MODULE_ALIAS['Part Numbers'],
  MODULE_ALIAS['Application Ids'],
  MODULES.MANUFACTURED_DEVICES,
  MODULES.MANUFACTURING_SHIPMENTS,
  MODULES.MANUFACTURING_RMA,
  MODULES.REPORTS_SCHEDULER,
  MODULES.REPORTS_CATEGORIES,
  MODULES.DEVICE_MANAGEMENT,
  MODULES.VEHICLE_MANAGEMENT,
  MODULES.ADMIN_REPORTS,
  MODULES.CARB_REPORTS,
  MODULES.CRASHBOXX_REPORTS,
  MODULES.DASHBOARDS_REPORTS,
  MODULES.REPORT_SCHEDULER_BETA,
  MODULES.REPORT_SCHEDULER_BASIC,
  MODULES.REPORT_SCHEDULER_STANDARD,
  MODULES.REPORT_SCHEDULER_ADMIN
];

/**
 * Define all the file permissions features
 */
export const _filePermissions = [
  MODULES.FILE_PERMISSIONS_SCRIPT,
  MODULES.FILE_PERMISSIONS_LMU_FW_CO_PRO,
  MODULES.FILE_PERMISSIONS_VBUS,
  MODULES.FILE_PERMISSIONS_BLUE_TOOTH,
  MODULES.FILE_PERMISSIONS_RADIO_FW,
  MODULES.FILE_PERMISSIONS_HOSTED_EDGE_OPKGS,
  MODULES.FILE_PERMISSIONS_CUSTOMER_DB_FILES,
  MODULES.FILE_PERMISSIONS_MDT,
  MODULES.FILE_PERMISSIONS_KEY_FILES
];

/**
 * Define all the VES Features
 */
export const _VES_FEATURES = [
  MODULES.VEHICLE_MANAGER,
  MODULES.VEHICLE_PROFILE,
  MODULES.OEM_VALIDATION_RULES
];

/**
 * Define all the Sensor Features
 */
export const _SENSOR_FEATURES = [
  MODULES.SENSOR_INSTALLER
];

/**
 * Define the ModBus Asset Files Feature
 */
export const _MODBUS_FEATURES = [
  MODULES.TOOLBOX_MODBUS
];

/**
 * Define the Service Enablement Edge App
 */
export const _SERVICE_ENABLEMENT_EDGEAPP = [
  MODULES.EA
];

/**
 * Map the file permission feature and permissions.
 */
export const _mappedFilePremissions =  {
  [MODULES.FILE_PERMISSIONS_SCRIPT]            : [Entity.lmuscript],
  [MODULES.FILE_PERMISSIONS_LMU_FW_CO_PRO]     : [Entity.lmufirmware],
  [MODULES.FILE_PERMISSIONS_VBUS]              : [Entity.vbusconfig, Entity.vbusfirmware, Entity.vbushostedapp],
  [MODULES.FILE_PERMISSIONS_BLUE_TOOTH]        : [Entity.bluetoothfirmware],
  [MODULES.FILE_PERMISSIONS_RADIO_FW]          : [Entity.radiofirmware],
  [MODULES.FILE_PERMISSIONS_HOSTED_EDGE_OPKGS] : [
    Entity.custompackagesedgeapp,
    Entity.custompackageshostedapp,
    Entity.custompackagesopkg
  ],
  [MODULES.FILE_PERMISSIONS_CUSTOMER_DB_FILES] : [
    Entity.customerdatabasedriverid,
    Entity.customerdatabasegeozone,
    Entity.customerdatabaseapn
  ],
  [MODULES.FILE_PERMISSIONS_MDT]       : [Entity.mdtconfig, Entity.mdtfirmware],
  [MODULES.FILE_PERMISSIONS_KEY_FILES] : [Entity.lmudigitalsignature]
};

/**
 * Define the CRUD actions used by the action-mapped feature permissions.
 */
const CRUD_ACTIONS = [
  Operation.Create,
  Operation.Read,
  Operation.Update,
  Operation.Delete
];

/**
 * Permission entity -> allowed actions (lowercase `Operation` values).
 */
export type EntityActionMap = Record<string, string[]>;

/**
 * Module (feature) name -> entity / action map.
 */
export type FeatureActionPermissionMap = Record<string, EntityActionMap>;

/**
 * Action-level permission mapping for features.
 *
 * By default, tagging a feature action auto-tags actions on every entity listed in
 * `mapFeaturePermissions`. A feature listed here instead auto-tags ONLY the
 * entity -> action pairs declared below, and its checkbox is enabled only when the
 * logged-in user holds those same actions.
 *
 * To opt a feature in:
 *   1. Add `[MODULES.<FEATURE>]: { [Entity.<entity>]: [Operation.<action>, ...] }` here.
 *   2. Do NOT add it to `mapFeaturePermissions` - its entity list is derived from this map.
 *   3. Keep the feature's entity list in ion-ctc-admin `Features.ts` (`mapFeaturePermissions`)
 *      equal to the keys declared here.
 */
export const mappedFeatureActionPermissions: FeatureActionPermissionMap = {
  [MODULES.REPORT_SCHEDULER_BASIC] : {
    [Entity.reportSchedule] : CRUD_ACTIONS
  },
  [MODULES.REPORT_SCHEDULER_STANDARD] : {
    [Entity.reportSchedule]      : CRUD_ACTIONS,
    [Entity.reportScheduleAdmin] : [Operation.Read]
  },
  [MODULES.REPORT_SCHEDULER_ADMIN] : {
    [Entity.reportSchedule]      : CRUD_ACTIONS,
    [Entity.reportScheduleAdmin] : [...CRUD_ACTIONS, Operation.Assign]
  }
};

/**
 * True when the module (feature name) uses action-level permission mapping.
 */
export const isActionMappedFeature = (moduleName: string): boolean =>
  Boolean(moduleName && mappedFeatureActionPermissions[moduleName]);

/**
 * Permission entities declared for the action-mapped module.
 * Returns an empty list when the module is not mapped.
 */
export const getMappedFeatureEntities = (moduleName: string): string[] =>
  Object.keys(mappedFeatureActionPermissions[moduleName] || {});

/**
 * Allowed actions (lowercase Operation values) for a module + permission entity.
 * Returns an empty list when the module / entity is not mapped.
 */
export const getMappedFeatureActions = (moduleName: string, entity: string): string[] =>
  mappedFeatureActionPermissions[moduleName]?.[entity] || [];

/**
 * Case-insensitive check whether a permission action may be auto-tagged
 * for the given module + permission entity.
 */
export const isMappedFeatureActionAllowed = (moduleName: string, entity: string, action: string): boolean =>
  getMappedFeatureActions(moduleName, entity).includes(action?.toLowerCase());

/**
 * True when the logged-in user holds the permission / actions declared for the
 * action-mapped module. Without an `action` every declared action is required
 * (the "All" checkbox); with an `action` only the entities mapping that action
 * are checked, and at least one entity must map it.
 */
export const hasMappedFeatureAccess = (Features, moduleName: string, action?: string): boolean => {
  const entries = Object.entries(mappedFeatureActionPermissions[moduleName] || {})
    .map(([entity, actions]) => [
      entity,
      action ? actions.filter((item) => item === action.toLowerCase()) : actions
    ] as const)
    .filter(([, actions]) => actions.length > 0);
  return entries.length > 0 && entries.every(([entity, actions]) =>
    actions.every((item) => Boolean(Features?.hasPermission?.(entity, item))));
};

/**
 * Returns the module names of all action-mapped features that currently have at least
 * one enabled module, walking the full feature tree (top level, child, grandchild, ...).
 */
export const getTaggedActionMappedFeatures = (features: GenericObject[] = []): string[] => {
  const tagged: string[] = [];
  const collect = (items: GenericObject[] = []) => {
    items?.forEach((feature) => {
      const moduleName = MAP_MODULES_FEATURE_IDS[feature?.id];
      if (isActionMappedFeature(moduleName) && feature?.modules?.some?.((module) => module?.enabled)) {
        tagged.push(moduleName);
      }
      collect(feature?.features);
    });
  };
  collect(features);
  return tagged;
};

/**
 * True when any tagged action-mapped feature other than `currentModuleName`
 * still maps the given entity + action, so the permission must be kept on un-tag.
 */
export const isMappedActionSharedByOtherFeature = ({
  taggedModuleNames,
  currentModuleName,
  entity,
  action
}: {
  taggedModuleNames: string[];
  currentModuleName: string;
  entity: string;
  action: string;
}): boolean => Boolean(taggedModuleNames
  ?.filter((name) => name !== currentModuleName)
  ?.some((name) => isMappedFeatureActionAllowed(name, entity, action)));

// determines if the feature has an enabled module for the given action (or its All action) ...
const hasEnabledAction = (feature: GenericObject, action: string): boolean => (
  Boolean(feature?.modules?.some?.(({name, enabled}) => (
    enabled && [action?.toLowerCase(), ALL_ACTION.toLowerCase()].includes(name?.toLowerCase())
  )))
);

/**
 * Determines if any tagged feature, other than the one being un-tagged, still grants the
 * given permission entity + action, in which case the permission must be kept. Features
 * such as All Reports, Report Categories and Report Scheduler all map the `report` entity,
 * so un-tagging one of them must not remove the permissions another still requires.
 *
 * ```typescript
 * import {isPermissionSharedByOtherFeature} from './ModuleActionHelpers';
 * ...
 *
 * // keeps report-read while all reports is still tagged...
 * isPermissionSharedByOtherFeature({
 *   features         : featureList,
 *   api              : Features,
 *   currentFeatureId : REPORTS_CATEGORIES,
 *   entity           : Entity.reports,
 *   action           : 'Read'
 * });
 * ```
 *
 * @param features          The Role Editor feature tree.
 * @param api               The application features API.
 * @param currentFeatureId  The feature being un-tagged.
 * @param entity            The permission entity being removed.
 * @param action            The permission action being removed.
 * @returns                 `true` when another tagged feature grants the entity + action,
 *                          otherwise `false`.
 */
export const isPermissionSharedByOtherFeature = ({
  features,
  api,
  currentFeatureId,
  entity,
  action
}: {
  features: GenericObject[];
  api: IApplicationFeatures;
  currentFeatureId: string;
  entity: string;
  action: string;
}): boolean => {
  const grantsPermission = (feature: GenericObject): boolean => {
    const moduleName = MAP_MODULES_FEATURE_IDS[feature?.id];
    const isGranted  = isActionMappedFeature(moduleName)
      ? isMappedFeatureActionAllowed(moduleName, entity, action) && hasEnabledAction(feature, ALL_ACTION)
      : Boolean(api?.getMappedPermissions(feature?.id)?.includes(entity)) && hasEnabledAction(feature, action);
    return (feature?.id !== currentFeatureId && isGranted) ||
      Boolean(feature?.features?.some?.(grantsPermission));
  };
  return Boolean(features?.some?.(grantsPermission));
};

/**
 * Map the permissions and features.
 */
const mapFeaturePermissions = {
  [MODULES.ADMIN_APPLICATIONS]             : [Entity.application],
  [MODULES.ADMIN_ASSETS]                   : [Entity.asset],
  [MODULES.ADMIN_AEMP_CONFIG]              : [Entity.aemppollerconfigurator],
  [MODULES.PROVISIONING_DEVICE_FILES]      : [Entity.filemeta, Entity.customerfile],
  [MODULES.ADMIN_DEVICE_CONFIGS]           : [Entity.deviceconfiguration],
  [MODULES.DEVICES]                        : [Entity.device],
  [MODULES.ADMIN_ACCOUNTS]                 : [Entity.account],
  [MODULES.PROVISIONING_DOWNLOAD_JOBS]     : [Entity.jobs, Entity.job, Entity.task],
  [MODULES.PROVISIONING_JOB_SEARCH_ENGINE] : [Entity.jobs, Entity.job, Entity.task],
  [MODULES.REPORTS_AVAILABLE_REPORTS]      : [Entity.reports],
  [MODULES.REPORTS_SCHEDULER]              : [Entity.reports],
  [MODULES.REPORTS_CATEGORIES]             : [Entity.reports],
  [MODULES.DEVICE_MANAGEMENT]              : [Entity.reports],
  [MODULES.VEHICLE_MANAGEMENT]             : [Entity.reports],
  [MODULES.ADMIN_REPORTS]                  : [Entity.reports],
  [MODULES.CARB_REPORTS]                   : [Entity.reports],
  [MODULES.CRASHBOXX_REPORTS]              : [Entity.reports],
  [MODULES.DASHBOARDS_REPORTS]             : [Entity.reports],
  [MODULES.ADMIN_PEG_LABELS]               : [Entity.peglabel],
  [MODULES.ADMIN_PEG_BEHAVIORS]            : [Entity.pegbehavior],
  [MODULES.ADMIN_ROLES]                    : [Entity.role],
  [MODULES.ADMIN_USERS]                    : [Entity.user],
  [MODULES.ADMIN_GROUPS]                   : [Entity.group],
  [MODULES.TOOLBOX_KEYFILE_BUILDER]        : [Entity.keyfilegenerator],
  [MODULES.TOOLBOX_MODBUS]                 : [Entity.filemeta, Entity.customerfile],
  [MODULES.MANUFACTURING_INVENTORY]        : [Entity.manufacturing],
  [MODULES.MANUFACTURED_DEVICES]           : [Entity.manufacturing],
  [MODULES.MANUFACTURING_APPLICATION_IDS]  : [Entity.appid, Entity.manufacturing],
  [MODULES.MANUFACTURING_PART_NUMBERS]     : [Entity.partnumber, Entity.manufacturing],
  [MODULES.MANUFACTURING_SHIPMENTS]        : [Entity.shipment],
  [MODULES.MANUFACTURING_RMA]              : [Entity.manufacturing],
  [MODULES.VEHICLE_MANAGER]                : [Entity.vin, Entity.vehicleidreport],
  [MODULES.VEHICLE_PROFILE]                : [Entity.vin],
  [MODULES.OEM_VALIDATION_RULES]           : [Entity.vin],
  [MODULES.SENTINEL]                       : [Entity.ddsar, Entity.subscription],
  [MODULES.TELECRYPT]                      : [Entity.telecrypt, Entity.inboundkey],
  [MODULES.BMS]                            : [Entity.bms],
  [MODULES.LMS]                            : [Entity.lms],
  [MODULES.CARGO]                          : [Entity.cargo],
  [MODULES.TPMS]                           : [Entity.tpms],
  [MODULES.WETS]                           : [Entity.wets],
  [MODULES.ICN]                            : [Entity.icn],
  [MODULES.WMS]                            : [Entity.wms],
  [MODULES.DOOR]                           : [Entity.door],
  [MODULES.BLE]                            : [Entity.bluetoothfirmware],
  [MODULES.IVT]                            : [Entity.ionvision, Entity.ion_vision_data],
  [MODULES.CARB]                           : [Entity.carb],
  [MODULES.MODBUS]                         : [Entity.modbus],
  [MODULES.SERVICES_CARB]                  : [Entity.carb],
  [MODULES.REPORT_SCHEDULER_BETA]          : [Entity.reports],
  ...Object.fromEntries(
    Object.keys(mappedFeatureActionPermissions).map((moduleName) => [
      moduleName,
      getMappedFeatureEntities(moduleName)
    ])
  )
};

/**
 * show / hide checkbox for the Dashboard and
 * Notifications.
 */
const showHideCheckbox = (moduleName: string, action: any): boolean => {
  if (moduleName === MODULES.MANUFACTURING_INVENTORY) {
    return ![ACTIONS.ALL, ACTIONS.CREATE, ACTIONS.ASSIGN].includes(action);
  }

  if (moduleName === MODULES.MANUFACTURING_RMA) {
    return ![
      ACTIONS.ALL,
      ACTIONS.CREATE,
      ACTIONS.UPDATE,
      ACTIONS.READ,
      ACTIONS.ASSIGN
    ].includes(action);
  }

  if (!_filePermissions.includes(moduleName) && !SINGLE_CHECKBOX_FEATURES.includes(moduleName)) {
    return action !== ACTIONS.ASSIGN;
  }
  return SINGLE_CHECKBOX_FEATURES.includes(moduleName) ? action === ALL_ACTION : true;
};

/**
   * Device password is the additional module under the Devices feature.
   * Define the module name here to use while select / de-select the
   * device password module.
   */
export const DEVICE_PASSWORD = 'Device Password';

/**
   * VBus Enabled is the additional module under the Devices feature.
   * Define the module name here to use while select / de-select the
   * VBus Enabled module.
   */
export const VBUS_ENABLED_STATUS = 'VBus Enabled Status';

/**
 * hasJob method to Check job, jobs and task entity and return boolean.
 */
export const hasJobPermission = (Features) => {
  const hasJob  = Features?.hasPermission?.(PermissionsOptions.JOB, Operation.Create);
  const hasJobs = Features?.hasPermission?.(PermissionsOptions.JOBS, Operation.Create);
  const hasTask = Features?.hasPermission?.(PermissionsOptions.TASK, Operation.Create);
  return hasJob || hasJobs || hasTask;
};

/**
 * Define the modules which is having Assign Action.
 */
export const assignActionModules = [];

/**
 * Check Assign Action.
 */
export const isAssignAction = (action): boolean => action?.toLowerCase() === Operation.Assign;

/**
 * checkPermissions method will Check for the job create,
 * and filemeta CRUD permission to enable / disable the actions
 * and return the boolean value accordingly.
 */
export const checkAllFeatureActions = ({
  Features,
  entities
}): GenericObject => {
  const hasCreateAccess = [];
  const hasReadAccess   = [];
  const hasUpdateAccess = [];
  const hasDeleteAccess = [];
  const hasAssignAccess = [];
  if (entities?.length) {
    entities?.forEach((entity) => {
      const hasCreate = Features?.hasPermission?.(entity, 'Create');
      const hasRead   = Features?.hasPermission?.(entity, 'Read');
      const hasUpdate = Features?.hasPermission?.(entity, 'Update');
      const hasDelete = Features?.hasPermission?.(entity, 'Delete');
      const hasAssign = Features?.hasPermission?.(entity, 'Assign');
      if (hasCreate) {
        hasCreateAccess.push(hasCreate);
      }
      if (hasRead) {
        hasReadAccess.push(hasRead);
      }
      if (hasUpdate) {
        hasUpdateAccess.push(hasUpdate);
      }
      if (hasDelete) {
        hasDeleteAccess.push(hasDelete);
      }
      if (hasAssign) {
        hasAssignAccess.push(hasAssign);
      }
    });
    return {
      Create : hasCreateAccess?.length,
      Read   : hasReadAccess?.length,
      Update : hasUpdateAccess?.length,
      Delete : hasDeleteAccess?.length,
      Assign : hasAssignAccess?.length
    };
  };
};

/**
 * Check All the actions and return true when all CRUD actions are true.
 */
export const isAllActionEnabled = (allActions: GenericObject, hasFilePermission = false): boolean => {
  const values = Object.values(allActions);
  const len    = hasFilePermission ? values?.length : values?.length -1;
  return values?.filter((item) => item)?.length === len;
};

/**
 * Generate the module Actions based on the conditions.
 * Example:
 * [
 *  {
 *    id      : 'Device-Create-1',
 *    name    : 'Create',
 *    enabled : false
 *  },
 *  {
 *    id      : 'Device-Read-2',
 *    name    : 'Read',
 *    enabled : false
 *  },
 *  {
 *    id      : 'Device-Update-3',
 *    name    : 'Update',
 *    enabled : false
 *  },
 *  {
 *    id      : 'Device-Delete-3',
 *    name    : 'Delete',
 *    enabled : false
 *   }
 * ]
 */
export const createModuleActions = ({
  Features,
  moduleName,
  actions,
  api,
  enableActionAlias = false
}): ModuleActions => {
  const modules                 = [];
  let featurePermissionActions  = {};
  const isFilePermissionFeature = _filePermissions.indexOf(moduleName) > -1;
  const isVESFeatures           = _VES_FEATURES.includes(moduleName);
  const isSensorsFeature        = _SENSOR_FEATURES.includes(moduleName);
  const isModbusFeature         = _MODBUS_FEATURES.includes(moduleName);
  const hasVESInternalRole      = api?.isUserHasInternalVESRole?.() || false;
  const hasServiceEdgeApp       = _SERVICE_ENABLEMENT_EDGEAPP.includes(moduleName);
  const hasSentinelFeature      = MODULES.SENTINEL.includes(moduleName);

  const entities = isFilePermissionFeature ? _mappedFilePremissions[moduleName] : mapFeaturePermissions[moduleName];
  featurePermissionActions = checkAllFeatureActions({
    Features,
    entities
  }) || {};
  
  actions?.forEach((action: ACTIONS, i: number) => {
    let hasPermission = action !== ALL_ACTION ? featurePermissionActions?.[action]
      : isAllActionEnabled(featurePermissionActions, isFilePermissionFeature);

    if (isActionMappedFeature(moduleName)) {
      hasPermission = hasMappedFeatureAccess(Features, moduleName, action === ALL_ACTION ? undefined : action);
    }

    if (PROTECTED_MODULES.includes(moduleName)) {
      hasPermission = Features.hasPermission(MODULE_TO_PERMISSION_ENTITY[moduleName], Operation[action]);
    }

    if (DEFAULT_MODULES.includes(moduleName)) {
      hasPermission = true;
    }

    if (isVESFeatures) {
      const isProfile  =  moduleName === MODULES.VEHICLE_PROFILE;
      const actions    = [ACTIONS.ALL, ACTIONS.CREATE, ACTIONS.DELETE, ACTIONS.UPDATE];
      const restricted = isProfile ? [...actions, ACTIONS.READ] : actions;
      hasPermission    = !restricted.includes(action) ||
      Features.hasPermission(mapFeaturePermissions[MODULES.VEHICLE_MANAGER[moduleName]], Operation[action]);
      if (hasVESInternalRole) {
        const allowedActions = isProfile ? [...actions, ACTIONS.READ] : [ACTIONS.READ, ACTIONS.UPDATE];
        hasPermission = allowedActions.includes(action);
      }
    }

    if (isSensorsFeature) {
      const restricted = [ACTIONS.ALL, ACTIONS.CREATE, ACTIONS.DELETE];
      hasPermission = !restricted.includes(action);
    }

    if (isModbusFeature) {
      const modBusRestrictedActions = [ACTIONS.ALL, ACTIONS.DELETE];
      hasPermission = !modBusRestrictedActions.includes(action);
    }

    if (hasSentinelFeature) {
      const allowedAction = [ACTIONS.READ];
      hasPermission       = allowedAction.includes(action);
    }

    if (hasServiceEdgeApp) {
      const serviceEdgeAppRestrictedActions = [ACTIONS.ALL, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.DELETE];
      hasPermission = !serviceEdgeAppRestrictedActions.includes(action);
    }
        
    modules.push({
      id            : `${moduleName}-${action}-${i}`,
      name          : action,
      enabled       : enableActionAlias,
      hasPermission : hasPermission,
      showCheckBox  : showHideCheckbox(moduleName, action)
    });
  });
  return modules;
};

const PROTECTED_MODULES = [
  MODULES.MANUFACTURING_INVENTORY
];

const MODULE_TO_PERMISSION_ENTITY = {
  [MODULES.MANUFACTURING_INVENTORY] : Entity.manufacturing
};

const DEFAULT_MODULES = [
  MODULES.DASHBOARD_PAGE
];

/**
 * Define the additional device modules here.
 */
export const additionalDevicesModuleList = [{
  id            : 'Device-Password-All',
  name          : DEVICE_PASSWORD,
  enabled       : false,
  hasPermission : true,
  showCheckBox  : true
}, {
  id            : 'VBus-Enabled-All',
  name          : VBUS_ENABLED_STATUS,
  enabled       : false,
  hasPermission : true,
  showCheckBox  : true
}];

/**
 * check isVbusEnabledStatus is enabled
 */
export const isVbusAccessEnabled = (feature: Feature): boolean => {
  const modules = feature?.name === MODULES?.DEVICES ? feature?.modules : [];
  return modules?.find((item) => item.name === VBUS_ENABLED_STATUS && item.enabled === true);
};

/**
 * Get the additional device module based on the given role.
 */
export const getAdditionalDevicesModule = (feature, isViewMode, isVewBusEnabled) => {
  const deviceModules =  [...additionalDevicesModuleList];
  if (isViewMode) {
    isVewBusEnabled = isVbusAccessEnabled(feature);
  }
  if (!isVewBusEnabled) {
    const VbusIndex = deviceModules?.findIndex((item) => item.name === VBUS_ENABLED_STATUS);
    delete deviceModules?.[VbusIndex];
  }
  return deviceModules;
};

/**
 * Checks if a specific provisioning feature and action are
 * enabled based on the provided feature configuration and API.
 * The child feature is read from the flat selections first, since a nested
 * ctc-admin feature can be selected without its parent gaining an entry.
 */
const hasProvisioningFeature = ({
  features,
  provisioningFeature,
  provisioningId,
  action,
  api
}) => {
  const featureId = api?.checkProvisioningId?.(provisioningId);
  const feature   = features?.[featureId] ||
    provisioningFeature?.features?.find?.(item => item?.id === featureId);
  return feature?.modules?.some?.(module => module?.id === action && module?.enabled) || false;
};

/**
 * Checks if a specific file permission action is enabled within a list of modules.
 */
const hasFilePermissionFeature = (modules, action) => {
  return modules?.some?.(module => module?.id?.indexOf?.(action) !== -1 && module?.enabled);
};

/**
 * Determines if provisioning is likely needed and if the necessary file
 * permissions are present based on the provided feature configurations and API.
 */
export const getProvisioningActions = (features, api, filePermissionsFeatures) => {
  const provisioningFeature = features?.[api?.checkProvisioningId?.(PROVISIONING_ACTIONS.PROVISIONING)];

  const hasCreateJob = hasProvisioningFeature({
    provisioningId : PROVISIONING_ACTIONS.PROVISIONING_DOWNLOAD_JOBS,
    action         : PROVISIONING_ACTIONS.JOBS_CREATE,
    features,
    provisioningFeature,
    api
  });

  const hasCreateFile = hasProvisioningFeature({
    provisioningId : PROVISIONING_ACTIONS.PROVISIONING_DEVICE_FILES,
    action         : PROVISIONING_ACTIONS.DEVICE_FILES_CREATE,
    features,
    provisioningFeature,
    api
  });

  let hasAssignFilePermission = false;
  let hasCreateFilePermission = false;
  let actionMessage           = '';
  let hasPartial              = true;
  let hasFull                 = true;
  const actions               = [];

  if (hasCreateJob || hasCreateFile) {
    const filePermissionsFeature = filePermissionsFeatures?.find?.(
      item => item?.id === api?.checkFilePermissionId?.(PROVISIONING_ACTIONS.FILE_PERMISSIONS)
    );

    if (filePermissionsFeature) {
      filePermissionsFeature?.features?.forEach?.(({modules = []}) => {
        if (hasCreateJob && !hasAssignFilePermission) {
          hasAssignFilePermission = hasFilePermissionFeature(modules, ACTIONS.ASSIGN);
        }
        if (hasCreateFile && !hasCreateFilePermission) {
          hasCreateFilePermission = hasFilePermissionFeature(modules, ACTIONS.CREATE);
        }
      });
    }
  }

  if (hasCreateJob) {
    hasPartial = hasAssignFilePermission;
  }
  if (hasCreateFile) {
    hasPartial = hasCreateFilePermission;
  }
  if (hasCreateJob && hasCreateFile) {
    hasFull = hasAssignFilePermission && hasCreateFilePermission;
  } else {
    hasFull = hasPartial;
  }

  if (hasCreateJob && !hasAssignFilePermission) {
    actions.push(ACTIONS.ASSIGN);
  }
  if (hasCreateFile && !hasCreateFilePermission) {
    actions.push(ACTIONS.CREATE);
  }
  if (actions?.length) {
    const action  = actions?.length === 1 ? actions[0] : actions?.join(' & ');
    actionMessage = translate('admin-roles:warningMessage.message', {action});
  }

  return {
    hasProvisioning   : hasCreateJob || hasCreateFile,
    hasFilePermission : hasFull,
    actionMessage
  };
};

/*
 * Validates whether all default Service Enablement feature permissions
 * are enabled for the selected role configuration.
 *
 * Filters the configured default features from both parent-level features and nested child features.
 * Verifies that all modules inside the matched features have the `enabled` flag set to true.
 * Returns: true -> when all required modules are enabled 
 *          false -> when any module is disabled or no matching features are found
 */
export const isServiceEnablementDefaultActionTagged = (featureList, api): boolean => {
  const safeFeatures              = featureList || [];
  const [
    DEVICES,
    ADMIN_ACCOUNTS,
    ADMIN_ASSETS
  ] = api?.getDefaultServiceEnablementFeatures();
  
  const devices = safeFeatures.find(feature => feature.id === DEVICES);
  const admin   = safeFeatures.find(feature => feature.name === 'Admin');

  const deviceModules   = devices?.modules || [];
  const accountsModules = getFeatureModules(admin?.features, ADMIN_ACCOUNTS);
  const assetsModules   = getFeatureModules(admin?.features, ADMIN_ASSETS);

  const hasDeviceRead   = hasFeatureActionTagged(deviceModules, 'Read');
  const hasDeviceUpdate = hasFeatureActionTagged(deviceModules, 'Update');
  const hasAccountsRead = hasFeatureActionTagged(accountsModules, 'Read');

  const hasAssetCreate = hasFeatureActionTagged(assetsModules, 'Create');
  const hasAssetUpdate = hasFeatureActionTagged(assetsModules, 'Update');
  
  return hasDeviceRead && hasDeviceUpdate && hasAccountsRead && hasAssetCreate && hasAssetUpdate;
};

/**
 * Returns the list of enabled Service Enablement feature.
 *
 * This method:
 * 1. Identifies the Service Enablement feature from the provided features list
 *    using the feature ID obtained from the API.
 * 2. Extracts and returns the enabled feature IDs for that feature
 *    using `getEnabledFeatureIds`.
 * 
 * Returns An array of enabled feature ids under the Service Enablement feature.
 */
export const taggedServiceEnablements = (features?: GenericObject, api: IApplicationFeatures) => {
  if (features?.length || api) {
    return getEnabledFeatureIds(features?.find(
      (item: GenericObject) => item.id === api?.getServiceEnablementFeatureId())
    );
  }
  return [];
};

/**
 * Returns the list of feature IDs that have at least one enabled module.
 *
 * This method iterates through the provided selectedFeature object,
 * filters features where at least one module is marked as enabled,
 * and returns an array of their corresponding feature IDs.
 *
 * Returns Array of feature IDs that have one or more enabled modules.
 */
export const getEnabledFeatureIds = (selectedFeature?: GenericObject): GenericObject => {
  if (selectedFeature?.features?.length) {
    return selectedFeature?.features
      ?.filter(feature =>
        feature.modules?.some(module => module.enabled)
      ).map(feature => feature.id) || [];
  }
  return [];
};

/**
 * Scrolls the specified container element to the top (or given position)
 * to ensure validation errors or important messages are visible to the user.
 *
 * @param {string} id - The DOM element id of the scrollable container
 * @param {number} scrollTop - The vertical scroll position (default is 0)
 */
export const scrollToTopAndShowError = (id, scrollTop = 0) => {
  const element = document.querySelector(`#${id}`);
  element?.scrollTo({
    top      : scrollTop,
    behavior : 'smooth'
  });
};

/**
 * Combines translated service and RMA error messages based on their presence.
 * Returns a single string with messages separated by a newline if both exist.
 */
export const dependencyErrorMessage = (hasServiceErr, hasRMAErr, hasSerErr) => {
  const serviceEblErr      = translate('common:editors.features.serviceEnablementWarningMsg');
  const rmaErr             = translate('common:editors.features.rmaWarningMsg');
  const serviceErr         = translate('common:editors.features.serviceWarningMsg');
  const warnings: string[] = [];
  if (hasServiceErr) {
    warnings.push(serviceEblErr);
  }
  if (hasRMAErr) {
    warnings.push(rmaErr);
  }
  if (hasSerErr) {
    warnings.push(serviceErr);
  }
  return warnings;
};

/**
   * Returns the selected inventory feature IDs excluding the MFG Shipment feature.
   */
export const taggedInventoryFeatures = (features?: GenericObject, api?: IApplicationFeatures) => {
  const selectedInventoryFeatures = getSelectedInventoryFeatures(features, api) || [];
  const featureIdToExclude        = api?.getMFGShipmentFeatureId();
  return selectedInventoryFeatures.filter((item) => {
    return featureIdToExclude !== undefined
      ? item !== featureIdToExclude
      : true;
  });
};

/**
 * Retrieves the enabled inventory feature IDs for the Manufacturing feature.
 * 
 * This function finds the Manufacturing feature from the provided feature list
 * using the feature ID and returns its enabled feature IDs.
 */
export const getSelectedInventoryFeatures = (features?: GenericObject, api?: IApplicationFeatures) => {
  if (features?.length || api) {
    return getEnabledFeatureIds(features?.find(
      (item: GenericObject) => item.id === api?.getManufacturingFeatureId())
    );
  }
  return [];
};

/**
 * Checks whether any provisioned device modules are enabled under the manufacturing feature.
 *
 * This function:
 * - Retrieves the manufacturing feature ID from the API.
 * - Finds the corresponding feature from the features list.
 * - Locates the provisioned devices feature within it.
 * - Returns true if at least one module is enabled, otherwise false.
 *
 * @param features - List of application features.
 * @param api - Application features API used to retrieve manufacturing feature ID.
 * @returns boolean indicating if any provisioned device module is enabled.
 */
export const hasProvisionedDevices = (
  features?: GenericObject[],
  api?: IApplicationFeatures
): boolean => {
  const manufacturingId = api?.getManufacturingFeatureId();
  
  const inventoryFeatures = features?.find(
    (item: GenericObject) => item.id === manufacturingId
  );

  const provisionDevices = inventoryFeatures?.features?.find(
    (item: GenericObject) => item.id === manufacturingId
  );

  return provisionDevices?.modules?.some(
    (module: GenericObject) => module?.enabled
  ) ?? false;
};

/**
 * Retrieves the "Services" feature object from the given features list
 * using the service feature identifier provided by the API.
 * 
 * @param features - List of feature objects
 * @param api - Application features API containing feature identifiers
 * @returns The matched Services feature object, or an empty array if not found
 */
export const getServicesFeatures = (features: GenericObject, api: IApplicationFeatures) => {
  const SERVICES = api?.getServiceFeature() || null;
  return SERVICES && features
    ? features?.find((item: GenericObject) => item.id === SERVICES) ?? []
    : [];
};

/**
 * Checks whether the CARB feature under Services has at least one enabled module.
 *
 * @param features - List of feature objects
 * @param api - Application features API containing feature identifiers
 * @returns true if any CARB module is enabled, otherwise false
 */
export const hasServicesCARBFeatureTagged = (features: GenericObject, api: IApplicationFeatures) => {
  const servicesFeatures = getServicesFeatures(features, api);
  const SERVICES_CARB    = api?.getServicesCARBFeatureId() ?? null;
  const CARBFeature      = SERVICES_CARB && servicesFeatures?.features?.find(
    (item: GenericObject) => item.id === SERVICES_CARB);
  return CARBFeature?.modules?.some((module: GenericObject) => module?.enabled) ?? false;
};

/**
 * Retrieves all modules associated with a specific feature ID.
 *
 * @param features - List of feature objects
 * @param id - Feature ID to filter.
 * @returns Flattened list of modules for the matching feature
 */
export const getFeatureModules = (features: GenericObject, id: string) => {
  return (features || [])
    ?.filter(feature => feature.id === id)
    ?.flatMap(feature => feature.modules || []);
};

/**
 * Checks if a given action is enabled within a list of modules.
 *
 * @param featureActionModule - List of modules to evaluate
 * @param action - Action name to check (e.g., 'Read', 'Write')
 * @returns true if the action exists and is enabled, otherwise false
 */
export const hasFeatureActionTagged = (featureActionModule: GenericObject, action: string) => {
  return featureActionModule?.some(module => module.name === action && module.enabled) ?? false;
};

/**
 * Validates whether all required service dependency features
 * (Devices, Accounts, Assets) have the 'Read' action enabled.
 *
 * This checks:
 * - Devices feature modules
 * - Accounts feature modules (under Admin)
 * - Assets feature modules (under Admin)
 *
 * @param features - List of feature objects
 * @param api - Application features API containing dependency identifiers
 * @returns true if all required features have 'Read' enabled, otherwise false
 */
export const hasServiceDependencyFeaturesTagged = (
  features: GenericObject,
  api: IApplicationFeatures
) => {
  const safeFeatures = features || [];
  const {
    DEVICES,
    ACCOUNTS,
    ASSETS
  } = api?.getServicesDependencyFeatures?.() || {};

  const devices = safeFeatures.find(feature => feature.id === DEVICES);
  const admin   = safeFeatures.find(feature => feature.name === 'Admin');

  const deviceModules   = devices?.modules || [];
  const accountsModules = getFeatureModules(admin?.features, ACCOUNTS);
  const assetsModules   = getFeatureModules(admin?.features, ASSETS);

  const hasDeviceRead   = hasFeatureActionTagged(deviceModules, 'Read');
  const hasAccountsRead = hasFeatureActionTagged(accountsModules, 'Read');
  const hasAssetRead    = hasFeatureActionTagged(assetsModules, 'Read');

  return hasDeviceRead && hasAccountsRead && hasAssetRead;
};

/**
 * Permission entities shared across every Service Enablement child Feature
 * (mirrors `defaultServiceEntities` in `ion-ctc-admin/src/security/Features.ts`).
 */
const SHARED_SERVICE_ENTITIES = [
  Entity.serviceProfile,
  Entity.deviceconfiguration,
  Entity.devicetype,
  Entity.vin
];

/**
 * `isFeatureHasMultiplePermissions`r method will Evaluates whether a given feature has single
 * or multiple permissions associated with it based on the
 * provided service enablements, inventory features, and feature type
 */
export const isFeatureHasMultiplePermissions = ({
  api,
  module,
  featurePermissionName,
  taggedServiceEnablements,
  taggedInventoryFeatures,
  carbDependentFeatureList,
  hasProvisionedDeviceTagged = false
}) => {
  let singleFeature    = false;
  let multipleFeatures = false;
  const hasSharedServiceEntity = SHARED_SERVICE_ENTITIES.includes(featurePermissionName);
  const hasManufacturing       = featurePermissionName === Entity.manufacturing;
  const hasCarb                = featurePermissionName === Entity.carb;
  const hasOnlyProvision       = hasProvisionedDeviceTagged && taggedInventoryFeatures?.length === 1;
  if (hasSharedServiceEntity) {
    multipleFeatures = true;
    singleFeature    = taggedServiceEnablements?.length === 1;
  }
  if (hasManufacturing && !hasOnlyProvision) {
    multipleFeatures = true;
    singleFeature    = taggedInventoryFeatures?.length === 1;
  }
  if (hasCarb && module && !module.enabled) {
    const {allRead, allUpdate} = carbDependentFeatureList;
    const {id, name}           = module;

    const isReadMode   = name === 'Read';
    const isUpdateMode = name === 'Update' && (id === SERVICES_CARB_UPDATE || id === CARB_UPDATE);

    if (isReadMode || isUpdateMode) {
      const isAllReadUpdate = isReadMode ? allRead : allUpdate;
      return {
        multiFeatures    : isAllReadUpdate,
        hasSingleFeature : !isAllReadUpdate
      };
    }
  }
  return {
    multiFeatures    : multipleFeatures,
    hasSingleFeature : singleFeature
  };
};

/**
 * Retrieves access information for CARB-dependent features (Services → CARB and
 * Service Enablement → CARB) from the user's feature list.
 * Returns the read, update, and full-access status for each CARB-related feature
 * based on the permissions assigned to the user's role.
 */
export const getCarbDependentFeatureStatus = (featureList, api) => {
  const serviceCarb           = api?.getServicesCARBFeatureId();
  const serviceEnablementCarb = api?.getCARBFeature();
  const targetIds             = [serviceCarb, serviceEnablementCarb];

  return (
    featureList
      ?.flatMap(group => group.features || [])
      .filter(feature => targetIds.includes(feature.id))
      .map(feature => {
        const readModule = feature.modules?.find(m => m.name === 'Read');
        const createModule = feature.modules?.find(m => m.name === 'Create');
        const updateModule = feature.modules?.find(m => m.name === 'Update');
        const deleteModule = feature.modules?.find(m => m.name === 'Delete');

        return {
          featureName     : feature.name,
          featureId       : feature.id,
          isReadEnabled   : !!readModule?.enabled,
          isCreateEnabled : !!createModule?.enabled,
          isUpdateEnabled : !!updateModule?.enabled,
          isDeleteEnabled : !!deleteModule?.enabled,
          hasFullAccess   : !!(createModule?.enabled && updateModule?.enabled &&
            readModule?.enabled && deleteModule?.enabled)
        };
      }) || []
  );
};

/**
 * Checks if all CARB-dependent features have Read and Update permissions.
 */
export const checkCarbDependency = (list = []) => {
  const allRead   = list.every(item => item.isReadEnabled);
  const allUpdate = list.every(item => item.isUpdateEnabled);
  return {
    allRead,
    allUpdate
  };
};
